import { useCallback, useEffect, useMemo, useState } from 'react';

import { INodeBasePanelElementConfig } from 'components/basic/NodeBase/NodeBase';
import useAbortController from 'hooks/useAbortController';
import { ComponentPropertyInputConfig, GeneratePropertyInputsConfig } from 'types/propertyGeneration';
import { useRoflStore } from 'store/roflStore';
import { InputProperty } from 'types/roflWorkflowActions';
import { ActionConfigValues } from 'types/rofl';

import useGenerateNodePanelDropDownConfig from './useGenerateNodePanelDropDownConfig';
import useGenerateFieldSelectorConfig from './useGenerateFieldSelectorConfig';
import useGenerateSingleLookupConfig from './useGenerateSingleLookupConfig';

/**
 * useGeneratePropertyInputs is used to:
 * - generate text for a containing node
 * - generate workflow component config from a set of workflow action properties
 */
function useGeneratePropertyInputs({
    nodeId,
    workflowActionService,
    workflowAction,
    workflow,
    handleUpdateWorkflowNode,
    propertyErrors,
    authedWithoutMissingScopes,
    call,
    loadingWorkflow,
    handleRequestError,
    recordFields,
}: GeneratePropertyInputsConfig) {
    const serviceTitle = workflowActionService?.service_display_name;
    const getSignal = useAbortController();

    const node = useRoflStore(state => state.contentData[nodeId]);

    /**
     * fieldInitialPropertyTextGenerated holds whether or not we have initially grabbed the node text
     * from a field (before it is even rendered)
     */
    const [
        fieldInitialPropertyTextGenerated,
        setFieldInitialPropertyTextGenerated
    ] = useState<Record<string, boolean>>({});
    const originalWorkflowActionConfigValues = workflow?.original?.action_config?.config;
    const currentWorkflowActionConfig = workflow?.current?.action_config;
    const currentWorkflowActionConfigValues = currentWorkflowActionConfig?.config;
    const [propertyInputText, setPropertyInputText] = useState<string>('');
    // if we are starting from scratch, or the current workflow node is undefined (reset)
    useEffect(() => {
        if (!workflow?.current) {
            setPropertyInputText('');
            setFieldInitialPropertyTextGenerated({});
        }
    }, [workflow, workflowAction]);

    /**
     *  generateNodePanelDropDownConfigFromInputProperty dynamically generates
     *  node panel dropdowns from the passed in config
     */
    const {
        generateNodePanelDropDownConfigFromInputProperty,
        isNodeDropDownConfig,
    } = useGenerateNodePanelDropDownConfig({
        serviceTitle,
        workflowAction,
        call,
        propertyErrors,
        authedWithoutMissingScopes,
        handleRequestError,
        fieldInitialPropertyTextGenerated,
        setFieldInitialPropertyTextGenerated
    });

    /**
     *  generateSingleLookup dynamically generates single text lookups from the passed in config
     */
    const { generateSingleLookupConfig, isSingleLookupConfig } = useGenerateSingleLookupConfig({
        workflowAction,
        propertyErrors,
        authedWithoutMissingScopes,
        call,
        fieldInitialPropertyTextGenerated,
        setFieldInitialPropertyTextGenerated,
        handleRequestError
    });


    /**
     *  useGenerateFieldSelectorConfig dynamically generates field selectors from the passed in config
     */
    const { generateFieldSelectorConfig, isFieldSelectorConfig } = useGenerateFieldSelectorConfig({
        serviceTitle,
        recordFields,
        propertyErrors,
        workflowAction,
        call,
        authedWithoutMissingScopes,
        handleRequestError,
        fieldInitialPropertyTextGenerated,
        setFieldInitialPropertyTextGenerated,
    });

    /**
     *  getPropertyInputComponentFcn returns the correct component generator function based off the inputProperty
     */
    const getPropertyInputComponentFcn = useCallback(
        (inputProperty: InputProperty):
        ((inputConfig: ComponentPropertyInputConfig) =>
          | INodeBasePanelElementConfig
          | INodeBasePanelElementConfig[]
          | undefined)
        | (() => null) => {
            if (isSingleLookupConfig(inputProperty)) {
                return generateSingleLookupConfig;
            } else if (isNodeDropDownConfig(inputProperty)) {
                return generateNodePanelDropDownConfigFromInputProperty;
            } else if (isFieldSelectorConfig(inputProperty)) {
                return generateFieldSelectorConfig;
            }
            return () => null;
        },
        [
            generateNodePanelDropDownConfigFromInputProperty,
            generateSingleLookupConfig,
            generateFieldSelectorConfig,
            isNodeDropDownConfig,
            isSingleLookupConfig,
            isFieldSelectorConfig,
        ]
    );


    /**
     *  generateWorkflowLookupPropertyInputs is the driver function to loop through a selected workflow action
     *  and generate the corresponding property inputs
     */
    const generateWorkflowLookupPropertyInputs = useCallback(() => {
        const elementConfigs: (INodeBasePanelElementConfig | null)[] = [];
        for (const inputProperty of (workflowAction?.properties || [])) {
            const currentConfig = workflow?.current?.action_config?.config as ActionConfigValues;
            const currentMetadata = workflow?.current?.action_config?.metadata;
            const propertyName = inputProperty.name;

            const nodePanelElementConfig = getPropertyInputComponentFcn(inputProperty)({
                inputProperty,
                actionMetadata: currentMetadata,
                actionMetadataProperty: currentMetadata?.properties[propertyName],
                workflowConfig: currentConfig,
                handleUpdateWorkflowNode
            });
            if (nodePanelElementConfig) {
                if (Array.isArray(nodePanelElementConfig)) {
                    elementConfigs.push(...nodePanelElementConfig);
                } else {
                    elementConfigs.push(nodePanelElementConfig);
                }
            }
        }

        return elementConfigs;
    }, [getPropertyInputComponentFcn, handleUpdateWorkflowNode, workflow, workflowAction?.properties]);

    const workflowPropertyInputs = useMemo(() => {
        return generateWorkflowLookupPropertyInputs();
    }, [generateWorkflowLookupPropertyInputs]);

    const getPropertyInputTextAbortSignal = getSignal();
    /**
     *  propertyInputText generates text from each field to pass up to the actionNode
     */
    useEffect(() => {
        const generatePropertyInputText = async () => {
            let text = '';

            // do nothing is the node unvalidated / not complete yet / or in the midst of editing
            if (loadingWorkflow || node.isIncomplete
                 || originalWorkflowActionConfigValues !== currentWorkflowActionConfigValues) {
                return;
            }
            const workflowActionConfig = currentWorkflowActionConfig;
            const getPropertyInputTextPromises: Promise<string>[] = [];
            for (const generatedPropertyInput of workflowPropertyInputs) {
                if (!generatedPropertyInput || !workflowActionConfig || !generatedPropertyInput.getTextForNode) {
                    continue;
                }
                getPropertyInputTextPromises.push(
                    generatedPropertyInput.getTextForNode(workflowActionConfig, getPropertyInputTextAbortSignal)
                );
            }
            const propertyInputTexts = await Promise.all(getPropertyInputTextPromises);
            for (const inputText of propertyInputTexts) {
                if (text && inputText) {
                    text += ',\n';
                }

                if (inputText) {
                    text += inputText;
                }
            }

            if (text && !getPropertyInputTextAbortSignal.aborted) {
                setPropertyInputText(text);
            }
        };
        generatePropertyInputText();
    }, [currentWorkflowActionConfig,
        currentWorkflowActionConfigValues,
        getPropertyInputTextAbortSignal,
        node.isIncomplete,
        originalWorkflowActionConfigValues,
        loadingWorkflow,
        workflowPropertyInputs
    ]
    );

    return { workflowPropertyInputs, propertyInputText };
}

export default useGeneratePropertyInputs;
